import { traducirError, useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, CampoNumero, CampoTexto, useAvisos, useFormulario, Ventana } from '@tallerya/ui';
import { useState } from 'react';
import { numeroDe } from '../productos-servicios/datos';
import { useClaveDeReintento } from '../ordenes/datos';
import { Pie } from '../ordenes/VentanasDeLinea';
import { errorDeMonto } from '../ordenes/VentanasDeMontos';
import { hoy, montoDe, useCambiosGasto, version, type Gasto, type MontoGasto } from './datos';

// Las ventanas del «•••» de un gasto (#143): el monto, el pago y eliminarlo. No
// tienen frame en el Figma: siguen a las de los montos de la orden.

// ------------------------------------------------------------ El monto

interface PropsMonto {
  gasto: Gasto | null;
  /** El monto como se leyó, con la versión que pide cambiarlo. */
  monto: MontoGasto | undefined;
  onCerrar: () => void;
}

/** Cómo se escribe el monto en el campo. Vacío es «sin monto», como el precio de una línea. */
function textoDeMonto(monto: MontoGasto): string {
  const n = montoDe(monto.amount);
  return n === null ? '' : String(n);
}

export function MontoDeGasto({ gasto, monto, onCerrar }: PropsMonto) {
  if (!gasto || !monto) return null;
  return <ContenidoMonto key={String(gasto.id)} gasto={gasto} monto={monto} onCerrar={onCerrar} />;
}

function ContenidoMonto({ gasto, monto, onCerrar }: { gasto: Gasto; monto: MontoGasto; onCerrar: () => void }) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosGasto();
  const claveDe = useClaveDeReintento();
  // La lectura con que se edita: la de al abrir, o la que trae «Recargar» después
  // de un cambio simultáneo (#171). Con la versión nueva cambia también la clave
  // de reintento: con la vieja, la API devolvería el mismo 409 guardado.
  const [leido, setLeido] = useState(monto);
  const versionLeida = version(leido.version);

  const form = useFormulario<{ amount: string }>({
    inicial: { amount: textoDeMonto(monto) },
    validar: (v) => errorDeMonto('amount', v.amount),
    guardar: (v) => {
      const nuevo = numeroDe(v.amount);
      return cambios.cambiarMonto(claveDe({ versionLeida, nuevo }), String(gasto.id), versionLeida, nuevo);
    },
    recargar: () => void recargar(),
  });

  /** Trae el monto que dejó la otra persona y la ventana sigue abierta, lista para guardar sobre él. */
  async function recargar() {
    try {
      const nuevo = await cambios.leerMonto(String(gasto.id));
      setLeido(nuevo);
      form.reiniciar({ amount: textoDeMonto(nuevo) });
    } catch {
      // Sin red: el aviso sigue ahí, con su «Recargar» para otro intento.
    }
  }

  async function guardar() {
    if (!(await form.enviar())) return;
    avisar({ titulo: t('admin.gastos.aviso.guardado') });
    onCerrar();
  }

  return (
    <Ventana
      abierta
      titulo={t('admin.gastos.cambiarMonto')}
      descripcion={gasto.description}
      acciones={<Pie guardando={form.guardando} onCerrar={onCerrar} onGuardar={() => void guardar()} />}
    >
      {form.aviso && <AvisoError aviso={form.aviso} />}
      <CampoNumero
        form={form}
        nombre="amount"
        etiqueta={t('admin.gastos.formulario.monto')}
        info={t('admin.gastos.formulario.montoInfo')}
        decimales={6}
      />
    </Ventana>
  );
}

// ------------------------------------------------------------ El pago

export function PagarGasto({ gasto, onCerrar }: { gasto: Gasto | null; onCerrar: () => void }) {
  if (!gasto) return null;
  return <ContenidoPago key={String(gasto.id)} gasto={gasto} onCerrar={onCerrar} />;
}

function ContenidoPago({ gasto, onCerrar }: { gasto: Gasto; onCerrar: () => void }) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosGasto();
  const claveDe = useClaveDeReintento();

  // Hoy se abre solo sin pagar: el «•••» de uno pagado ofrece quitar la marca.
  // Si algún día se ofrece corregir la fecha, arranca con la que tiene.
  const form = useFormulario<{ paidOn: string }>({
    inicial: { paidOn: gasto.paidOn ?? hoy() },
    guardar: (v) => cambios.pagar(claveDe({ gasto: gasto.id, dia: v.paidOn }), String(gasto.id), v.paidOn),
    camposPorCodigo: { 'expense.pago_antes_de_compra': 'paidOn', 'expense.fecha_fuera_de_rango': 'paidOn' },
  });

  async function guardar() {
    if (!(await form.enviar())) return;
    avisar({ titulo: t('admin.gastos.aviso.pagado') });
    onCerrar();
  }

  return (
    <Ventana
      abierta
      titulo={t('admin.gastos.marcarPagado')}
      descripcion={gasto.description}
      acciones={
        <Pie
          guardando={form.guardando}
          etiqueta={t('admin.gastos.marcarPagado')}
          onCerrar={onCerrar}
          onGuardar={() => void guardar()}
        />
      }
    >
      {form.aviso && <AvisoError aviso={form.aviso} />}
      <CampoTexto
        form={form}
        nombre="paidOn"
        etiqueta={t('admin.gastos.pago.fecha')}
        info={t('admin.gastos.pago.info')}
        tipo="date"
        obligatorio
      />
    </Ventana>
  );
}

// ------------------------------------------------------------ Eliminar

export function EliminarGasto({
  gasto,
  onCerrar,
  onEliminado,
}: {
  gasto: Gasto | null;
  onCerrar: () => void;
  /** Después de eliminarlo, además de cerrar: la ficha sale de la pantalla. */
  onEliminado?: (() => void) | undefined;
}) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosGasto();
  const [eliminando, setEliminando] = useState(false);
  const [problema, setProblema] = useState<string | null>(null);

  function cerrar() {
    if (eliminando) return;
    setProblema(null);
    onCerrar();
  }

  async function eliminar() {
    if (!gasto) return;
    setEliminando(true);
    setProblema(null);
    try {
      const error = await cambios.eliminar(String(gasto.id));
      if (error) {
        setProblema(error.code ? traducirError(error.code, t) : t('ui.formulario.aviso.sinRespuesta'));
        return;
      }
    } catch {
      // Sin red la petición lanza; sin esto «Cancelar» quedaría apagado (#124).
      setProblema(t('ui.formulario.aviso.sinRespuesta'));
      return;
    } finally {
      setEliminando(false);
    }
    avisar({ titulo: t('admin.gastos.aviso.eliminado') });
    onCerrar();
    onEliminado?.();
  }

  return (
    <Ventana
      abierta={gasto !== null}
      titulo={t('admin.gastos.eliminar.titulo')}
      descripcion={gasto && t('admin.gastos.eliminar.texto', { gasto: gasto.description })}
      acciones={
        <>
          <Boton variante="texto" disabled={eliminando} onClick={cerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton variante="peligro" disabled={eliminando} aria-busy={eliminando} onClick={() => void eliminar()}>
            {t('admin.gastos.eliminar.titulo')}
          </Boton>
        </>
      }
    >
      {problema && <AvisoError aviso={{ titulo: t('admin.gastos.noSePudo'), texto: problema }} />}
    </Ventana>
  );
}
